import React, { MutableRefObject, useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { fullScreen } from '@/components/use-shared-element';
import {
  getVariationEntryFallback,
  getVariationImagesFallback,
} from '@/utils/experience-utils';
import { GradientModal } from '@/components/gradient-modal';
import {
  Experience,
  ExperienceInfoRequest,
  ExperienceInfoResponse,
  useExperienceApi,
} from '@/webapi/use-experience-api';
import { CONTROL } from '@/features/details/shared';
import { hash } from '@/utils/cache';
import { PreviewImageVerticalGallery } from '@/features/shared/experience-images-enhanced-modal/preview-image-vertical-gallery';
import { Spinner } from '@/Spinner';
import { HorizontalSlider } from '@/components/horizontal-slider';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';

export interface ExperienceImagesEnhancedModalProps {
  fromRef: MutableRefObject<any>;
  visible: boolean;
  setVisible: (s: boolean) => void;
  currentExperience?: Experience;
  onExperienceInfoChanged?: (info: ExperienceInfoResponse) => void;
}

export function ExperienceImagesEnhancedModal({
  fromRef,
  visible,
  setVisible,
  currentExperience,
  onExperienceInfoChanged,
}: ExperienceImagesEnhancedModalProps) {
  const [previewLinks, setPreviewLinks] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const { updateExperienceInfo, getExperienceInfo, generatePreviewLink } =
    useExperienceApi();

  const isFavoriteStarUpdate = useFeatureBit(
    FeatureBit.FAVORITE_STAR_EXPERIENCE_IMAGE,
  );

  const [data, setData] = useState<ExperienceInfoResponse | null>(null);

  const modalCfg = useMemo(() => getModalConfig(fromRef), [fromRef]);

  useEffect(() => {
    if (currentExperience?.id && visible) {
      setLoading(true);
      setIsInitialLoad(true);
      getExperienceInfo(currentExperience.id)
        .then((experienceInfo) => {
          setData({
            ...experienceInfo,
            variationImages: {
              ...(experienceInfo.variationImages || {}),
              ...(currentExperience.variationImages || {}),
            },
          });
          setLoading(false);
          setTimeout(() => setIsInitialLoad(false), 0);
        })
        .catch((error) => {
          console.error(`Error fetching experience info:`, error);
          setLoading(false);

          if (currentExperience) {
            const fallbackData: ExperienceInfoResponse = {
              id: currentExperience.id,
              name: currentExperience.name,
              isHidden: currentExperience.isHidden || false,
              description: currentExperience.description || ``,
              labels:
                currentExperience.labels?.map((label: string) => ({
                  label,
                  selected: true,
                })) || [],
              beforeImages: currentExperience.beforeImages || [],
              afterImages: currentExperience.afterImages || [],
              variationImages: currentExperience.variationImages || {},
            };
            setData(fallbackData);
          }
        });
    }
  }, [currentExperience?.id, visible]);

  const filteredVariants = useMemo(
    () =>
      currentExperience?.variants?.filter(
        (variant) => variant.name !== CONTROL,
      ) || [],
    [currentExperience?.variants],
  );

  useEffect(() => {
    if (!currentExperience?.variants || !currentExperience?.quickPreviewUrl) {
      return;
    }

    const links: Record<string, string> = {};

    const controlVariant = currentExperience.variants.find(
      (v) => v.name === CONTROL,
    );
    links[CONTROL] = currentExperience.quickPreviewUrl;

    const promises = currentExperience?.variants?.map(async (variant) => {
      const previewHash = hash(
        `${currentExperience.quickPreviewUrl}-${variant.id}`,
      );

      if (previewHash?.length > 5) {
        try {
          const response = await generatePreviewLink(
            currentExperience.id,
            variant.id,
            currentExperience.quickPreviewUrl,
            variant.id,
          );
          try {
            if (variant.name === CONTROL && response.link) {
              links[CONTROL] = response.link;
            } else {
              links[variant.id] = response.link;
            }
          } catch (error) {
            console.error(`Error processing preview link:`, error);
          }
        } catch (error1) {
          console.error(
            `Error generating preview link for variant:`,
            variant.id,
            error1,
          );
        }
      }
      return Promise.resolve();
    });

    if (currentExperience.isThemeTest && links[CONTROL]) {
      promises.push(
        generatePreviewLink(
          currentExperience.id,
          controlVariant?.id || ``,
          currentExperience.quickPreviewUrl,
        )
          .then((response) => {
            try {
              const parsedUrl = new URL(response.link);
              parsedUrl.searchParams.set(`preview_theme_id`, ``);
              links[CONTROL] = parsedUrl.toString();
            } catch (error) {
              console.error(`Error processing control preview link:`, error);
            }
          })
          .catch((error) => {
            console.error(`Error generating control preview link:`, error);
          }),
      );
    }

    Promise.all(promises)
      .then(() => {
        setPreviewLinks(links);
      })
      .catch((error) => {
        console.error(`Failed to load all preview links:`, error);
      });
  }, [currentExperience]);

  const onUpdate = async () => {
    if (!data) return;
    const updated = { ...data, labels: [] } as ExperienceInfoRequest;
    updated.labels = data?.labels
      ?.filter((l) => l.selected)
      .map((l) => l.label);

    try {
      await updateExperienceInfo(updated);
    } catch (error) {
      console.error(`Error updating experience info:`, error);
    }
  };

  const onBeforeImagesChanged = (images: string[], favorite?: string) => {
    const currentFavorite = data.variationImages?.[CONTROL]?.favorite;
    const newFavorite = favorite !== undefined ? favorite : currentFavorite;

    const updatedVariationImages = {
      ...data.variationImages,
      [CONTROL]: {
        ...data.variationImages?.[CONTROL],
        favorite: newFavorite,
      },
    };

    const updatePayload = {
      variationImages: updatedVariationImages,
      beforeImages: images,
    };

    setData((draft) => {
      if (!draft) return null;
      return {
        ...draft,
        ...updatePayload,
      };
    });

    if (onExperienceInfoChanged) {
      onExperienceInfoChanged({
        ...data,
        ...updatePayload,
      });
    }
  };

  const onVariationImagesChanged = (
    images: string[],
    id: string,
    favorite?: string,
  ) => {
    const currentVariation = data.variationImages?.[id];

    const newVariationEntry = {
      ...currentVariation,
      values: images,
      favorite: favorite !== undefined ? favorite : currentVariation?.favorite,
    };

    const updatedVariationImages = {
      ...data.variationImages,
      [id]: newVariationEntry,
    };

    const updatePayload = {
      variationImages: updatedVariationImages,
    };

    setData((draft) => {
      if (!draft) return null;
      return {
        ...draft,
        ...updatePayload,
      };
    });
    if (onExperienceInfoChanged) {
      onExperienceInfoChanged({
        ...data,
        ...updatePayload,
      });
    }
  };

  useEffect(() => {
    if (data && !isInitialLoad) {
      onUpdate();
    }
  }, [data?.beforeImages, data?.variationImages]);

  if (loading || !data) {
    return (
      <GradientModal
        {...modalCfg}
        isVisible={visible}
        onClose={() => setVisible(false)}
        header={null}
        footer={null}
      >
        <SpinnerWrapper>
          <Spinner size={100} />
        </SpinnerWrapper>
      </GradientModal>
    );
  }

  return (
    <GradientModal
      {...modalCfg}
      isVisible={visible}
      onClose={() => setVisible(false)}
      header={null}
      footer={null}
    >
      <Wrapper onClick={() => setVisible(false)}>
        <HorizontalSlider
          className="horizontal-wrapper"
          arrowsLeft={4}
          arrowsRight={4}
          extraStartPadding={5}
          extraEndPadding={5}
          sizeLeftChevron={20}
          sizeRightChevron={20}
          speed={4}
        >
          <Column isFavoriteStarUpdate={isFavoriteStarUpdate}>
            <Chip>Control</Chip>
            {previewLinks?.[CONTROL] && (
              <PreviewLink target="_blank" href={previewLinks[CONTROL]}>
                Preview on site
              </PreviewLink>
            )}
            <PreviewImageVerticalGallery
              onImageListChanged={onBeforeImagesChanged}
              maxImages={5}
              allowUpload
              isFavoriteStarUpdate={isFavoriteStarUpdate}
              initialValue={data?.beforeImages || []}
              favoriteImage={data?.variationImages?.[CONTROL]?.favorite}
            />
          </Column>
          {filteredVariants.map((variant) => (
            <React.Fragment key={variant.id}>
              <VsColumn isFavoriteStarUpdate={isFavoriteStarUpdate}>
                VS
              </VsColumn>
              <Column isFavoriteStarUpdate={isFavoriteStarUpdate}>
                <Chip>{variant.name}</Chip>
                {previewLinks?.[variant.id] && (
                  <PreviewLink target="_blank" href={previewLinks[variant.id]}>
                    Preview on site
                  </PreviewLink>
                )}
                <PreviewImageVerticalGallery
                  onImageListChanged={(images, favorite) =>
                    onVariationImagesChanged(images, variant.id, favorite)
                  }
                  maxImages={5}
                  allowUpload
                  isFavoriteStarUpdate={isFavoriteStarUpdate}
                  initialValue={getVariationImagesFallback(
                    data.variationImages,
                    variant.id,
                  )}
                  favoriteImage={
                    getVariationEntryFallback(data.variationImages, variant.id)
                      .favorite
                  }
                />
              </Column>
            </React.Fragment>
          ))}
        </HorizontalSlider>
      </Wrapper>
    </GradientModal>
  );
}

const Wrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: rgba(0, 0, 0, 0);
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;

  .horizontal-wrapper {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    .scroll-button {
      height: 5.25rem;
      width: 5.25rem;
    }

    .scroller {
      padding: 0;
      margin: 0;
    }

    .start-space {
      margin-left: auto;
    }
    .end-space {
      margin-right: auto;
    }
  }
`;

const SpinnerWrapper = styled.div`
  height: 100%;
  width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PreviewLink = styled.a`
  font-family: Inter, serif;
  color: #808991;
  text-decoration: underline;

  &:hover {
    color: black;
  }
`;
const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  min-width: 350px;
  max-width: 35%;
  align-items: center;
  align-self: flex-start;
  height: ${({ isFavoriteStarUpdate }) =>
    isFavoriteStarUpdate ? `calc(100dvh - 3rem)` : `calc(100dvh - 12rem)`};
  margin-top: ${({ isFavoriteStarUpdate }) =>
    isFavoriteStarUpdate ? `3rem` : `12rem`};
`;
const VsColumn = styled.div`
  font-family: Inter, serif;
  font-size: 16px;
  padding-top: 12px;
  letter-spacing: -0.56px;
  text-align: center;
  color: #808991;
  min-width: 5rem;
  max-width: 5rem;
  align-self: flex-start;
  height: ${({ isFavoriteStarUpdate }) =>
    isFavoriteStarUpdate ? `calc(100dvh - 3rem)` : `calc(100dvh - 12rem)`};
  margin-top: ${({ isFavoriteStarUpdate }) =>
    isFavoriteStarUpdate ? `3rem` : `12rem`};
`;
const Chip = styled.div`
  padding: 12px 34px;
  width: fit-content;
  border-radius: 30px;
  background-color: #fff;

  font-family: Inter, serif;
  font-size: 16px;
  letter-spacing: -0.56px;
  text-align: center;
  color: #808991;
`;

function getModalConfig(fromRef: React.MutableRefObject<any>) {
  const targetPosAndSize = fullScreen();

  return {
    targetPosAndSize,
    closeTop: `3rem`,
    closeRight: `3rem`,
    fromStyle: {
      borderRadius: `5rem`,
      backgroundColor: `transparent`,
      padding: `0`,
    },
    toStyle: {
      borderRadius: `0`,
      backgroundColor: `transparent`,
      padding: `0`,
    },
    showBackdrop: true,
    fromRef,
    background: `0,0,0,0`,
    horizontalPadding: 0,
    padding: 0,
    contentPadding: 0,
    isFullScreenTransparent: true,
    headerTop: 0,
    shadowFixScrollerMargin: 0,
    shadowFixScrollerPadding: 0,
    noTopContentSpacing: true,
    noBottomContentSpacing: true,
    overflowTop: { heightInRem: 0 },
    overflowBottom: { heightInRem: 0, turnPointInPercent: 0 },
    backdropBlur: 20,
    closeStyle: {
      background: `white`,
      borderRadius: `100%`,
      padding: 10,
      boxShadow: `rgba(100, 100, 111, 0.35) 0px 7px 29px 0px`,
    },
  };
}
